import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  Circle,
  Smartphone,
  Monitor,
  Tablet,
  Download,
  Calendar,
  Globe,
  Clock,
  Mail,
  Activity,
} from 'lucide-react';

interface UserRow {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  ruta: number;
  assigned_ruta: number | null;
  unit_id: string | null;
  is_coringa: boolean;
}

interface SessionRow {
  id: string;
  started_at: string;
  last_seen_at: string;
  ended_at: string | null;
  ip: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  device_type: string | null;
  os: string | null;
  browser: string | null;
  user_agent: string | null;
}

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

type RangeKey = 'today' | '7d' | '30d' | '60d' | 'custom';

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return '<1min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${minutes}min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
}

function DeviceIcon({ type }: { type: string | null }) {
  if (type === 'mobile') return <Smartphone className="w-4 h-4" />;
  if (type === 'tablet') return <Tablet className="w-4 h-4" />;
  return <Monitor className="w-4 h-4" />;
}

function deviceLabel(type: string | null): string {
  if (type === 'mobile') return 'Celular';
  if (type === 'tablet') return 'Tablet';
  return 'Computador';
}

function sessionDuration(row: SessionRow): number {
  const start = new Date(row.started_at).getTime();
  const end = new Date(row.ended_at || row.last_seen_at).getTime();
  return Math.max(0, Math.floor((end - start) / 1000));
}

function isSessionOngoing(row: SessionRow): boolean {
  if (row.ended_at) return false;
  return Date.now() - new Date(row.last_seen_at).getTime() < ONLINE_THRESHOLD_MS;
}

function toCsv(rows: SessionRow[]): string {
  const header = [
    'Data',
    'Hora',
    'Duração (min)',
    'Cidade',
    'Estado',
    'País',
    'Dispositivo',
    'Sistema',
    'Navegador',
    'IP',
  ];
  const lines = rows.map((r) => {
    const start = new Date(r.started_at);
    const duration = Math.round(sessionDuration(r) / 60);
    return [
      start.toLocaleDateString('pt-BR'),
      start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      String(duration),
      r.city || '',
      r.region || '',
      r.country || '',
      deviceLabel(r.device_type),
      r.os || '',
      r.browser || '',
      r.ip || '',
    ]
      .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
      .join(',');
  });
  return [header.join(','), ...lines].join('\n');
}

export default function SellerActivityDetail() {
  const { user } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [target, setTarget] = useState<UserRow | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const isAllowed = user?.role === 'manager' || user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId || !isAllowed) return;
      setLoading(true);
      setError(null);
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, email, display_name, role, ruta, assigned_ruta, unit_id, is_coringa')
          .eq('id', userId)
          .maybeSingle();
        if (userError) throw userError;
        if (!userData) {
          setError('Vendedor não encontrado.');
          setLoading(false);
          return;
        }
        if (user?.role === 'manager' && userData.unit_id !== user.unit_id) {
          setError('Este vendedor pertence a outra unidade.');
          setLoading(false);
          return;
        }
        if (cancelled) return;
        setTarget(userData as UserRow);

        const { data: sessionRows, error: sessionError } = await supabase
          .from('user_sessions')
          .select('id, started_at, last_seen_at, ended_at, ip, city, region, country, device_type, os, browser, user_agent')
          .eq('user_id', userId)
          .order('started_at', { ascending: false })
          .limit(500);
        if (sessionError) throw sessionError;
        if (cancelled) return;
        setSessions((sessionRows as SessionRow[]) || []);
      } catch (err: any) {
        console.error(err);
        setError(err?.message || 'Não foi possível carregar o histórico.');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, user?.role, user?.unit_id, isAllowed]);

  const rangeBounds = useMemo(() => {
    let from: Date | null = null;
    let to: Date | null = null;
    if (range === 'today') { from = startOfToday(); }
    else if (range === '7d') { from = daysAgo(6); }
    else if (range === '30d') { from = daysAgo(29); }
    else if (range === '60d') { from = daysAgo(59); }
    else if (range === 'custom') {
      if (customFrom) from = new Date(`${customFrom}T00:00:00`);
      if (customTo) {
        to = new Date(`${customTo}T00:00:00`);
        to.setDate(to.getDate() + 1);
      }
    }
    return { from, to };
  }, [range, customFrom, customTo]);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      const started = new Date(s.started_at).getTime();
      if (rangeBounds.from && started < rangeBounds.from.getTime()) return false;
      if (rangeBounds.to && started >= rangeBounds.to.getTime()) return false;
      return true;
    });
  }, [sessions, rangeBounds]);

  const totals = useMemo(() => {
    let secondsToday = 0;
    let secondsWeek = 0;
    let secondsMonth = 0;
    const today = startOfToday().getTime();
    const week = daysAgo(6).getTime();
    const month = daysAgo(29).getTime();
    sessions.forEach((s) => {
      const started = new Date(s.started_at).getTime();
      const dur = sessionDuration(s);
      if (started >= today) secondsToday += dur;
      if (started >= week) secondsWeek += dur;
      if (started >= month) secondsMonth += dur;
    });
    return { secondsToday, secondsWeek, secondsMonth };
  }, [sessions]);

  const lastSeen = sessions[0];
  const online = !!lastSeen && isSessionOngoing(lastSeen);

  const handleDownload = () => {
    if (!target) return;
    const csv = toCsv(filtered);
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const label = (target.display_name || target.email.split('@')[0]).replace(/\s+/g, '_');
    link.href = url;
    link.download = `acessos_${label}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isAllowed) {
    return <div className="p-8 text-center text-gray-500">Área restrita à gestão da unidade.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/dashboard/team-activity')}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para a equipe
        </button>
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!target) return null;

  const name = target.display_name || target.email.split('@')[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/dashboard/team-activity')}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para a equipe
        </button>
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-700"
        >
          <Download className="w-4 h-4" />
          Exportar planilha
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <Activity className="w-3.5 h-3.5" />
              Atividade do vendedor
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{target.email}</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                {target.role === 'motoboy' ? 'Motoboy' : target.role === 'manager' ? 'Gestor' : 'Vendedor'}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                Ruta {target.assigned_ruta ?? target.ruta}
              </span>
            </div>
          </div>
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${
              online
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}
          >
            <Circle className={`w-2.5 h-2.5 ${online ? 'fill-emerald-500 text-emerald-500 animate-pulse' : 'text-gray-400'}`} />
            {online ? 'Online agora' : lastSeen ? `Visto em ${formatDateTime(lastSeen.last_seen_at)}` : 'Sem acessos'}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Conectado hoje</div>
            <div className="text-lg font-semibold text-gray-900">{formatDuration(totals.secondsToday)}</div>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Últimos 7 dias</div>
            <div className="text-lg font-semibold text-gray-900">{formatDuration(totals.secondsWeek)}</div>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Últimos 30 dias</div>
            <div className="text-lg font-semibold text-gray-900">{formatDuration(totals.secondsMonth)}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Calendar className="w-4 h-4 text-gray-500" />
            Período do histórico
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                { key: 'today', label: 'Hoje' },
                { key: '7d', label: '7 dias' },
                { key: '30d', label: '30 dias' },
                { key: '60d', label: '60 dias' },
                { key: 'custom', label: 'Personalizado' },
              ] as { key: RangeKey; label: string }[]
            ).map((btn) => (
              <button
                key={btn.key}
                onClick={() => setRange(btn.key)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  range === btn.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                }`}
              >
                {btn.label}
              </button>
            ))}
            {range === 'custom' && (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                />
                <span className="text-xs text-gray-400">até</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">
            Histórico de acessos
            <span className="ml-2 text-xs text-gray-400">{filtered.length} acesso{filtered.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            Nenhum acesso encontrado no período selecionado.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((row, idx) => {
              const ongoing = isSessionOngoing(row);
              const dur = sessionDuration(row);
              return (
                <li key={row.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      ongoing
                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                        : 'bg-gray-50 text-gray-500 border border-gray-200'
                    }`}>
                      <DeviceIcon type={row.device_type} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatDateTime(row.started_at)}
                        </div>
                        {idx === 0 && !ongoing && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                            Último acesso
                          </span>
                        )}
                        {ongoing && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Em andamento
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatDuration(dur)} conectado
                        </span>
                        <span className="inline-flex items-center gap-1">
                          {deviceLabel(row.device_type)}
                          {row.os ? ` · ${row.os}` : ''}
                          {row.browser ? ` · ${row.browser}` : ''}
                        </span>
                        {(row.city || row.region) && (
                          <span className="inline-flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {[row.city, row.region, row.country].filter(Boolean).join(' / ')}
                          </span>
                        )}
                        {row.ip && (
                          <span className="text-gray-400">IP {row.ip}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-gray-400">
        A localização é aproximada, calculada a partir do endereço de internet no momento do acesso. Registros com mais de 60 dias são removidos automaticamente.
      </p>
    </div>
  );
}
